import React, { useState, useRef, useLayoutEffect, useEffect } from 'react';
import { createPortal } from 'react-dom';

// Small click-toggled "(i)" info tooltip for explaining confusing inputs.
// The popover is positioned with fixed coordinates and clamped to the viewport so it
// escapes any scrollable/overflow-hidden panel and never gets cut off.
// Usage: <InfoTip text="What this setting does." />
function InfoTip({ text }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null);
  const btnRef = useRef(null);
  const popRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const handleOutside = (event) => {
      if (
        btnRef.current && !btnRef.current.contains(event.target)
        && popRef.current && !popRef.current.contains(event.target)
      ) {
        setOpen(false);
      }
    };
    const handleScroll = () => setOpen(false);
    document.addEventListener('mousedown', handleOutside);
    window.addEventListener('scroll', handleScroll, true);
    window.addEventListener('resize', handleScroll);
    return () => {
      document.removeEventListener('mousedown', handleOutside);
      window.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', handleScroll);
    };
  }, [open]);

  useLayoutEffect(() => {
    if (!open || !btnRef.current) return;
    const rect = btnRef.current.getBoundingClientRect();
    const margin = 10;
    const width = Math.min(260, window.innerWidth - margin * 2);
    let left = rect.left + rect.width / 2 - width / 2;
    left = Math.max(margin, Math.min(left, window.innerWidth - width - margin));
    const popHeight = popRef.current ? popRef.current.offsetHeight : 120;
    let top = rect.top - popHeight - 8; // prefer above the icon
    if (top < margin) top = rect.bottom + 8; // not enough room above -> below
    setPos({ left, top, width });
  }, [open, text]);

  return (
    <span className="vix-infotip" ref={btnRef}>
      <button
        type="button"
        className="vix-infotip-btn"
        aria-label="More information"
        title="More information"
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          setOpen((value) => !value);
        }}
      >
        i
      </button>
      {open && createPortal(
        <span
          ref={popRef}
          className="vix-infotip-pop"
          role="tooltip"
          style={pos
            ? { position: 'fixed', left: pos.left, top: pos.top, width: pos.width }
            : { position: 'fixed', visibility: 'hidden' }}
        >
          {text}
        </span>,
        document.body,
      )}
    </span>
  );
}

export default InfoTip;
